import React, {useEffect, useState} from "react";
import {Button, Card, Input, Space, Typography, Popconfirm} from 'antd';
import {QRCodeSVG} from 'qrcode.react';
import {
    BellOutlined,
    CopyOutlined,
    DeleteOutlined,
    EyeInvisibleOutlined,
    LoginOutlined,
    LogoutOutlined,
    MobileOutlined,
    PlusOutlined,
    QrcodeOutlined,
    ScanOutlined,
    TeamOutlined,
    UserOutlined,
    WarningOutlined,
    ClearOutlined
} from '@ant-design/icons';
import AvatarDisplay from '../components/AvatarDisplay';

const {Title, Text, Paragraph} = Typography;

const baseURL = window.location.origin + window.location.pathname;

const ReceptionTerminal = ({
                               availableAvatars,
                               players,
                               api,
                               showToast,
                               showSuccess,
                               showError,
                               showWarning,
                               showInfo
                           }) => {
    const [nfcTagId, setNfcTagId] = useState('');
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [groupName, setGroupName] = useState('');
    const [playerNames, setPlayerNames] = useState(['']);
    const [mode, setMode] = useState('login');
    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const [lastLoggedPlayer, setLastLoggedPlayer] = useState(null);
    const [showQRCode, setShowQRCode] = useState(false);

    const generateMobileTerminalURL = (nfcId) => {
        return `${baseURL}?viewId=mobile&nfc=${nfcId}`;
    };

    const generateMobileRegistrationURL = (nfcId) => {
        return `${baseURL}?viewId=register-players&nfc=${nfcId}`;
    };

    const handleNfcScan = async () => {
        if (!nfcTagId.trim()) {
            showWarning('Proszę wprowadzić ID tagu NFC 📝');
            return;
        }

        setLoading(true);
        showInfo(`${mode === 'logout' ? 'Wylogowywanie' : 'Sprawdzanie'} grupy...`);

        try {
            if (mode === 'logout') {
                await api.logoutPlayer(nfcTagId);
                showSuccess('Grupa została wylogowana! 👋');

                if (lastLoggedPlayer && lastLoggedPlayer.nfcTagId === nfcTagId) {
                    setShowQRCode(false);
                    setLastLoggedPlayer(null);
                }

                setNfcTagId('');
            } else {
                showInfo('Tag NFC gotowy do logowania! Wprowadź dane grupy 👥');
            }
        } catch (error) {
            showError(`Błąd ${mode === 'logout' ? 'wylogowania' : 'skanowania'}: ${error.message} ❌`);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async () => {
        if (!selectedAvatar) {
            showWarning('Proszę wybrać awatar przed logowaniem! 👤');
            return;
        }

        if (!groupName.trim()) {
            showWarning('Proszę wprowadzić nazwę grupy! 👥');
            return;
        }

        const validPlayerNames = playerNames
            .filter(name => name.trim() !== '')
            .map(name => name.trim());

        const uniquePlayerNames = [...new Set(validPlayerNames)];

        if (uniquePlayerNames.length === 0) {
            showWarning('Proszę wprowadzić przynajmniej jedno imię gracza! 👤');
            return;
        }

        if (uniquePlayerNames.length > 6) {
            showWarning('Maksymalnie 6 graczy w grupie! 👥');
            return;
        }

        if (uniquePlayerNames.length !== validPlayerNames.length) {
            showWarning('Imiona graczy muszą być unikalne! 🚫');
            return;
        }

        setLoading(true);
        showInfo(`Logowanie grupy "${groupName}" z ${uniquePlayerNames.length} graczami...`);

        try {
            await api.loginPlayer(nfcTagId, selectedAvatar.numericId, groupName, uniquePlayerNames);
            showSuccess(`Grupa "${groupName}" została zalogowana! 🎉`);

            setLastLoggedPlayer({
                nfcTagId: nfcTagId,
                avatar: selectedAvatar,
                groupName: groupName,
                playerNames: uniquePlayerNames,
                mobileURL: generateMobileTerminalURL(nfcTagId)
            });
            setShowQRCode(true);

            setTimeout(() => {
                showInfo('Kod QR wygenerowany! Zeskanuj telefonem 📱');
            }, 1000);

            setNfcTagId('');
            setSelectedAvatar(null);
            setGroupName('');
            setPlayerNames(['']);
        } catch (error) {
            showError(`Błąd logowania: ${error.message} ❌`);
        } finally {
            setLoading(false);
        }
    };

    const handleResetGame = async () => {
        setResetLoading(true);
        showInfo('Resetowanie stanu gry... ⚠️');

        try {
            await api.resetGameState();
            showSuccess('Stan gry został zresetowany! Wszystkie wyniki zostały usunięte. 🔄');

            // Ukryj QR kod jeśli był widoczny
            setShowQRCode(false);
            setLastLoggedPlayer(null);

            // Wyczyść formularz
            setNfcTagId('');
            setSelectedAvatar(null);
            setGroupName('');
            setPlayerNames(['']);

        } catch (error) {
            showError(`Błąd resetowania gry: ${error.message} ❌`);
        } finally {
            setResetLoading(false);
        }
    };

    const addPlayerField = () => {
        if (playerNames.length < 6) {
            setPlayerNames([...playerNames, '']);
        } else {
            showWarning('Maksymalnie 6 graczy w grupie! 👥');
        }
    };

    const removePlayerField = (index) => {
        if (playerNames.length > 1) {
            const newPlayerNames = playerNames.filter((_, i) => i !== index);
            setPlayerNames(newPlayerNames);
        }
    };

    const updatePlayerName = (index, name) => {
        const newPlayerNames = [...playerNames];
        newPlayerNames[index] = name;
        setPlayerNames(newPlayerNames);
    };

    const showQRForPlayer = (player) => {
        setLastLoggedPlayer({
            nfcTagId: player.nfcTagId || player.id,
            avatar: player.avatar,
            groupName: player.groupName,
            playerNames: player.playerNames || [],
            mobileURL: generateMobileTerminalURL(player.nfcTagId || player.id)
        });
        setShowQRCode(true);

        showInfo(`Kod QR dla grupy "${player.groupName}" 📱`);
    };

    useEffect(() => {
        if (showQRCode) {
            const timer = setTimeout(() => {
                setShowQRCode(false);
            }, 60000);

            return () => clearTimeout(timer);
        }
    }, [showQRCode, showInfo]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (mode === 'login' && nfcTagId && !selectedAvatar) {
                handleNfcScan();
            } else if (mode === 'login' && selectedAvatar && groupName) {
                handleLogin();
            } else if (mode === 'logout') {
                handleNfcScan();
            }
        }
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setNfcTagId('');
        setSelectedAvatar(null);
        setGroupName('');
        setPlayerNames(['']);
    };

    const isLoginFormValid = () => {
        return selectedAvatar &&
            groupName.trim() &&
            playerNames.some(name => name.trim());
    };

    return (
        <div className="container">
            <div className="layout-grid">
                <div style={{gridColumn: '1 / -1'}}>
                    <Title level={1} className="tablet"
                           style={{color: 'var(--color-white)', textAlign: 'center', marginBottom: 32}}>
                        <BellOutlined/> Terminal Recepcyjny
                    </Title>

                    {/* Mode Selector */}
                    <Card className="reception-card" style={{marginBottom: 24}}>
                        <Space size="middle" style={{width: '100%', justifyContent: 'center'}}>
                            <Button
                                type={mode === 'login' ? 'primary' : 'default'}
                                icon={<LoginOutlined/>}
                                onClick={() => handleModeChange('login')}
                                size="large"
                                className="button"
                            >
                                Logowanie Grupy
                            </Button>
                            <Button
                                type={mode === 'logout' ? 'primary' : 'default'}
                                icon={<LogoutOutlined/>}
                                onClick={() => handleModeChange('logout')}
                                size="large"
                                className="button"
                            >
                                Wylogowanie
                            </Button>
                        </Space>
                    </Card>

                    {/* NFC Section */}
                    <Card className="reception-card" style={{marginBottom: 24}}>
                        <Title level={3} style={{color: 'var(--color-white)'}}>
                            <MobileOutlined/> Skanowanie tagu NFC
                        </Title>
                        <Space.Compact style={{width: '100%'}} size="large">
                            <Input
                                placeholder="Wprowadź ID tagu NFC..."
                                value={nfcTagId}
                                onChange={(e) => setNfcTagId(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={loading}
                                className="input"
                                size="large"
                            />
                            <Button
                                type="primary"
                                onClick={handleNfcScan}
                                disabled={loading}
                                loading={loading}
                                icon={<ScanOutlined/>}
                                size="large"
                                className="button primary"
                            >
                                {mode === 'login' ? 'Sprawdź Tag' : 'Wyloguj'}
                            </Button>
                        </Space.Compact>
                    </Card>

                    {/* Login Form */}
                    {mode === 'login' && nfcTagId && !loading && (
                        <>
                            {/* Combined Group Info and Registration QR */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr',
                                gap: '24px',
                                marginBottom: 24
                            }}>
                                {/* Group Info Section */}
                                <Card className="reception-card">
                                    <Title level={3} style={{color: 'var(--color-white)'}}>
                                        <TeamOutlined/> Informacje o grupie
                                    </Title>
                                    <Input
                                        placeholder="Nazwa grupy (np. 'Drużyna Alfa')"
                                        value={groupName}
                                        onChange={(e) => setGroupName(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        disabled={loading}
                                        maxLength={30}
                                        className="input"
                                        size="large"
                                        style={{marginBottom: 16}}
                                    />

                                    <Title level={4} style={{color: 'var(--color-white)'}}>
                                        <UserOutlined/> Imiona graczy ({playerNames.filter(n => n.trim()).length}/6):
                                    </Title>

                                    <Space direction="vertical" style={{width: '100%'}} size="middle">
                                        {playerNames.map((name, index) => (
                                            <Space.Compact key={index} style={{width: '100%'}}>
                                                <Input
                                                    placeholder={`Imię gracza ${index + 1}`}
                                                    value={name}
                                                    onChange={(e) => updatePlayerName(index, e.target.value)}
                                                    disabled={loading}
                                                    maxLength={20}
                                                    className="input"
                                                />
                                                {playerNames.length > 1 && (
                                                    <Button
                                                        type="default"
                                                        onClick={() => removePlayerField(index)}
                                                        disabled={loading}
                                                        icon={<DeleteOutlined/>}
                                                        danger
                                                    />
                                                )}
                                            </Space.Compact>
                                        ))}

                                        {playerNames.length < 6 && (
                                            <Button
                                                type="dashed"
                                                onClick={addPlayerField}
                                                disabled={loading}
                                                icon={<PlusOutlined/>}
                                                block
                                            >
                                                Dodaj gracza
                                            </Button>
                                        )}
                                    </Space>
                                </Card>

                                {/* Group Registration QR Section */}
                                <Card className="reception-card">
                                    <Title level={4} style={{color: 'var(--color-white)'}}>
                                        <TeamOutlined/> Rejestracja graczy na urządzeniu mobilnym:
                                    </Title>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginBottom: 16,
                                        padding: 16,
                                        backgroundColor: 'white',
                                        borderRadius: 8,
                                        width: 'fit-content',
                                        margin: '0 auto 16px'
                                    }}>
                                        <QRCodeSVG
                                            value={generateMobileRegistrationURL(nfcTagId)}
                                            size={200}
                                            level="M"
                                            includeMargin={true}
                                        />
                                    </div>
                                </Card>
                            </div>

                            {/* Avatar Selection */}
                            <Card className="reception-card" style={{marginBottom: 24}}>
                                <Title level={3} style={{color: 'var(--color-white)'}}>
                                    <TeamOutlined/> Wybierz awatar grupy:
                                </Title>
                                <div className="avatar-selector">
                                    {availableAvatars
                                        .filter(avatar => avatar.available)
                                        .map(avatar => (
                                            <div
                                                key={avatar.id}
                                                className={`avatar-option ${selectedAvatar?.id === avatar.id ? 'selected' : ''}`}
                                                onClick={() => setSelectedAvatar(avatar)}
                                                title={avatar.name || `Awatar ${avatar.numericId}`}
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    padding: '12px',
                                                    borderRadius: 'var(--radius-8)',
                                                    border: selectedAvatar?.id === avatar.id
                                                        ? '3px solid var(--color-light-green)'
                                                        : '2px solid var(--white-30)',
                                                    backgroundColor: selectedAvatar?.id === avatar.id
                                                        ? 'var(--white-20)'
                                                        : 'var(--white-10)',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    minWidth: '80px'
                                                }}
                                            >
                                                <AvatarDisplay
                                                    avatarId={avatar.numericId}
                                                    size={48}
                                                    showBorder={false}
                                                />
                                                <Text
                                                    style={{
                                                        color: 'var(--color-white)',
                                                        fontSize: '12px',
                                                        marginTop: '4px',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {avatar.name || `#${avatar.numericId}`}
                                                </Text>
                                            </div>
                                        ))}
                                </div>
                            </Card>

                            {/* Login Summary */}
                            {isLoginFormValid() && (
                                <Card className="reception-card" style={{marginBottom: 24}}>
                                    <Title level={4} style={{color: 'var(--color-white)'}}>
                                        📋 Podsumowanie logowania:
                                    </Title>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        marginBottom: '16px'
                                    }}>
                                        <AvatarDisplay
                                            avatarId={selectedAvatar.numericId}
                                            size={40}
                                        />
                                        <Paragraph style={{color: 'var(--color-white)', margin: 0}}>
                                            <Text strong>Grupa:</Text> "{groupName}"<br/>
                                            <Text strong>Gracze:</Text> {playerNames.filter(n => n.trim()).join(', ')}
                                        </Paragraph>
                                    </div>
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={handleLogin}
                                        disabled={loading}
                                        loading={loading}
                                        icon={<TeamOutlined/>}
                                        block
                                        className="button primary large"
                                    >
                                        {loading ? 'Logowanie...' : `Zaloguj grupę "${groupName}"`}
                                    </Button>
                                </Card>
                            )}
                        </>
                    )}

                    {/* QR Code Section */}
                    {showQRCode && lastLoggedPlayer && (
                        <Card className="reception-card" style={{marginBottom: 24}}>
                            <Title level={3} style={{color: 'var(--color-white)'}}>
                                <MobileOutlined/> Kod QR dla Grupy
                            </Title>
                            <div style={{textAlign: 'center'}}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '12px',
                                    marginBottom: '16px'
                                }}>
                                    <AvatarDisplay
                                        avatarId={lastLoggedPlayer.avatar.numericId}
                                        size={48}
                                    />
                                    <Paragraph style={{color: 'var(--color-white)', margin: 0}}>
                                        <Text strong>Grupa:</Text> "{lastLoggedPlayer.groupName}"<br/>
                                        <Text strong>Gracze:</Text> {lastLoggedPlayer.playerNames?.join(', ')}<br/>
                                        <Text strong>NFC ID:</Text> {lastLoggedPlayer.nfcTagId}<br/>
                                        <Text strong>Instrukcja:</Text> Zeskanuj kod QR telefonem
                                    </Paragraph>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginBottom: 16,
                                    padding: 16,
                                    backgroundColor: 'white',
                                    borderRadius: 8,
                                    width: 'fit-content',
                                    margin: '0 auto 16px'
                                }}>
                                    <QRCodeSVG
                                        value={lastLoggedPlayer.mobileURL}
                                        size={200}
                                        level="M"
                                        includeMargin={true}
                                    />
                                </div>

                                <Space size="middle">
                                    <Button
                                        onClick={() => setShowQRCode(false)}
                                        icon={<EyeInvisibleOutlined/>}
                                        className="button secondary"
                                    >
                                        Ukryj QR
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            navigator.clipboard.writeText(lastLoggedPlayer.mobileURL);
                                            showSuccess('Link skopiowany do schowka! 📋');
                                        }}
                                        icon={<CopyOutlined/>}
                                        className="button secondary"
                                    >
                                        Kopiuj Link
                                    </Button>
                                </Space>

                                <Paragraph style={{color: 'var(--color-white)', fontSize: 12, marginTop: 16}}>
                                    {lastLoggedPlayer.mobileURL}
                                </Paragraph>
                            </div>
                        </Card>
                    )}

                    {/* Current Players */}
                    <Card className="reception-card" style={{marginBottom: 24}}>
                        <Title level={3} style={{color: 'var(--color-white)'}}>
                            <TeamOutlined/> Aktualnie zalogowane grupy ({players.length}):
                        </Title>
                        {players.length === 0 ? (
                            <Text style={{color: 'var(--color-white)'}}>Brak zalogowanych grup</Text>
                        ) : (
                            <Space direction="vertical" style={{width: '100%'}} size="middle">
                                {players.map(player => (
                                    <div key={player.id} className="ranking-row">
                                        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                            <AvatarDisplay
                                                avatarId={player.avatar?.numericId || player.avatarId || 1}
                                                size={40}
                                            />
                                            <div>
                                                <Text strong style={{color: 'var(--color-white)'}}>
                                                    "{player.groupName}"
                                                </Text><br/>
                                                <Text style={{color: 'var(--color-white)', opacity: 0.8}}>
                                                    Gracze: {player.playerNames?.join(', ') || 'Brak danych'}
                                                </Text><br/>
                                                <Text style={{color: 'var(--color-white)', opacity: 0.6, fontSize: 12}}>
                                                    Zalogowana: {new Date(player.loginTime).toLocaleTimeString('pl-PL')}
                                                </Text>
                                            </div>
                                        </div>
                                        <div style={{textAlign: 'right'}}>
                                            <Text strong style={{color: 'var(--color-white)'}}>
                                                {player.totalScore} pkt
                                            </Text><br/>
                                            <Button
                                                size="small"
                                                onClick={() => showQRForPlayer(player)}
                                                icon={<QrcodeOutlined/>}
                                                title="Pokaż kod QR dla mobilnego terminala"
                                                className="button secondary small"
                                            >
                                                QR
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </Space>
                        )}
                    </Card>

                    {/* Admin Reset Section */}
                    <Card className="reception-card" style={{marginBottom: 24, borderColor: '#ff4d4f'}}>
                        <Title level={4} style={{color: '#ff4d4f', marginBottom: 16}}>
                            <WarningOutlined style={{marginRight: 8}} />
                            Funkcje administracyjne
                        </Title>
                        <Space size="middle">
                            <Popconfirm
                                title="Resetowanie stanu gry"
                                description={
                                    <div>
                                        <Text strong style={{color: '#ff4d4f'}}>UWAGA!</Text><br/>
                                        To działanie usunie:<br/>
                                        • Wszystkich zalogowanych graczy<br/>
                                        • Wszystkie wyniki ze wszystkich dołków<br/>
                                        • Cały stan gry<br/><br/>
                                        <Text strong>Czy na pewno chcesz kontynuować?</Text>
                                    </div>
                                }
                                onConfirm={handleResetGame}
                                okText="Tak, resetuj grę"
                                cancelText="Anuluj"
                                okButtonProps={{ danger: true }}
                                placement="top"
                            >
                                <Button
                                    danger
                                    size="large"
                                    loading={resetLoading}
                                    disabled={resetLoading}
                                    icon={<ClearOutlined />}
                                    className="button"
                                >
                                    {resetLoading ? 'Resetowanie...' : 'Resetuj stan gry'}
                                </Button>
                            </Popconfirm>
                            <Text style={{color: 'var(--white-60)', fontSize: '12px'}}>
                                Użyj tylko w przypadku konieczności rozpoczęcia gry od nowa
                            </Text>
                        </Space>
                    </Card>
                    
                </div>
            </div>
        </div>
    );
};

export default ReceptionTerminal;